import {
  Box,
  Dialog,
  Divider,
  Grid,
  IconButton,
  SelectChangeEvent,
  Skeleton,
  Theme,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ReportsRightLayout } from './index';
import { TextDatePicker } from '../onboarding/DatePickerWithTextFeild';
import FormMultiSelect from '../../lib/formik/FormMultiSelect';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import { Location } from 'history';
import dayjs from "dayjs";
import { reportDownload } from '../Reports/report-utils';
import { RootStateType } from '../../redux-store/reducers';
import * as Yup from 'yup';
import { clientStatementDownload, createClientStatement, getClientStatementList, updateClientStatementStatus } from '../../redux-store/actions/reports';
import MFSelectField from '../../lib/formik/SelectField';
import { CONTENT_TYPE, REPORTS_FILTER_TYPE_OPTIONS, REQUESTED_REPORTS, REQUESTED_REPORTS_KEYS } from '../../utils/constant';
import { DialogCloseIcon, InfoIconOutlined, RefreshIcon, YellowWarning } from '../../assets/SvgIcons';
import ClientStatementTable from '../ClientStatementTable';
import { ClientStatementReportsList, CreateClientStatementResponseBody, GetClientStatementListResponseBody, UpdateClientStatementStatusResponseBody } from '../../redux-store/types/onBoarding';
import { Order, PaginationObj, SortingObj } from '../investorOverview/types';
import MFCheckbox from '../../lib/formik/Checkbox';

interface Values {
  filterType: string;
  isHeldAway: boolean;
  filterTypeOptions: Array<{ key: string; value: string; }>;
  familyName: string;
  endDate: any;
  reportSections: Array<string>
}

const ClientStatement = ({
  handleSideSectionDisabled
}: {
  location: Location<{ investorId: string; investorName: string }>;
  handleSideSectionDisabled: (value: boolean) => void
}): JSX.Element => {
  const formikRef = useRef<FormikProps<Values>>(null);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isInitiating, setIsInitiating] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)
  const [isTableDataLoading, setTableDataLoading] = useState<boolean>(false)
  const [tableData, setTableData] = useState<Array<ClientStatementReportsList>>([])
  const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
    order: "desc",
    orderBy: "createdAt"
  })
  const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
    currentPage: 0,
    rowsPerPage: 5,
    totalRows: 0
  })
  const [selectedJobIdForDownload, setSelectedJobIdForDownload] = useState<string | null>(null)

  const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);

  const todayDate = new Date();
  const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").subtract(1, 'day').format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

  const { iwappRmId = "", investorClientId = "", familyId = "", familyName = "", panNo = "", clientInterfaceCode = "" } = useSelector((state: RootStateType) => state.id);
  const { selectedPortfolioObj } = useSelector((state: RootStateType) => state.selectedPortfiolio)

  const handleNoInvestorDialogClose = () => {
    setNoInvestorDialogVisible(false)
  }

  const fetchTableData = async ({
    currentPage,
    fundsPerPage,
    sort,
    order
  }: {
    currentPage: number;
    fundsPerPage: number;
    sort: string;
    order: Order
  }) => {
    const payload = {
      rmId: `${iwappRmId}`,
      sort,
      order,
      currentPage,
      rowsPerPage: fundsPerPage,
      clientCode: `${investorClientId ?? ""}`
    }

    const {
      count = 0,
      reports = []
    } = await (dispatch(getClientStatementList(payload)) as unknown as Promise<GetClientStatementListResponseBody>)

    setTableData(reports)
    setTableSortingObj({
      order,
      orderBy: sort
    })
    setTablePaginationObj({
      currentPage: currentPage - 1,
      rowsPerPage: fundsPerPage,
      totalRows: count
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setTableDataLoading(true)

        await fetchTableData({
          currentPage: 1,
          fundsPerPage: 5,
          sort: tableSortingObj.orderBy,
          order: tableSortingObj.order
        })
      } catch (e) {
        console.log("Error:", e)
      } finally {
        setTableDataLoading(false)
      }
    }

    fetchData()

  }, [])

  const initialValues: Values = {
    filterType: "PORTFOLIO",
    isHeldAway: false,
    filterTypeOptions: familyId ? REPORTS_FILTER_TYPE_OPTIONS : REPORTS_FILTER_TYPE_OPTIONS.filter(item => `${item?.key}`.toLowerCase() !== "family"),
    familyName: "",
    endDate: formattedBusinessDate,
    // reportSections: Object.keys(REQUESTED_REPORTS).filter(key => key !== "4")
    reportSections: REQUESTED_REPORTS_KEYS
  };

  const validationSchemaClientStatement = Yup.object().shape({
    filterType: Yup
      .string()
      .required('Please select a Filter type'),
    // reportSections: Yup
    //   .array()
    //   .of(
    //     Yup
    //       .string()
    //   )
    //   .min(1, 'Please select a report section'),
    endDate: Yup
      .date()
      .required('As on Date is required')
      .typeError('As on Date is required')
      .max(formattedBusinessDate, "As on Date should not be greater than system's date"),
  })

  const handleRefresh = async () => {
    try {
      setTableDataLoading(true)

      const payload = {
        rmId: `${iwappRmId}`,
        clientCode: `${investorClientId ?? ""}`
      }

      const {
        count = 0,
        reports = []
      } = await (dispatch(updateClientStatementStatus(payload)) as unknown as Promise<UpdateClientStatementStatusResponseBody>)

      setTableData(reports)
      setTableSortingObj({
        order: "desc",
        orderBy: "createdAt"
      })
      setTablePaginationObj({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: count
      })
    } catch (e) {
      console.log("Error:", e)
    } finally {
      setTableDataLoading(false)
    }
  }

  const handleSubmit = async ({
    filterType,
    isHeldAway,
    endDate,
    reportSections
  }: Values) => {
    try {
      setIsInitiating(true)
      handleSideSectionDisabled(true)

      const idMap: { [key: string]: string } = {
        FAMILY: `${familyId ?? ""}`,
        CUSTOMER: `${panNo ?? ""}`,
        PORTFOLIO: `${clientInterfaceCode}`
      }

      const payload = {
        clientCode: `${investorClientId ?? ""}`,
        rmId: `${iwappRmId}`,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
        accountType: `${selectedPortfolioObj?.portfolioAccountType ?? ""}`,
        requested_reports: (reportSections ?? []).map(Number),
        consolidatedLevel: filterType === "CUSTOMER" ? "PAN" : filterType,
        custFilterValue: idMap[filterType ?? ""] ?? "",
        heldAway: isHeldAway ? 'Y' : 'N'
      }

      const response = await (dispatch(createClientStatement(payload)) as unknown as Promise<CreateClientStatementResponseBody>)

      if (response?.isSuccess) {
        enqueueSnackbar(`${response?.message ?? ""}`, {
          variant: 'success',
          autoHideDuration: 5000,
        });

        handleRefresh()

        return
      }
      if (!response?.isSuccess) {
        enqueueSnackbar(`${response?.message ?? ""}`, {
          variant: 'error',
          autoHideDuration: 5000,
        });

        return
      }
    } catch (error) {
      console.log("Error: ", error)
    } finally {
      setIsInitiating(false)
      handleSideSectionDisabled(false)
    }
  }

  const handleTableSort = async (orderBy: string, order: Order) => {
    try {
      setTableDataLoading(true)

      await fetchTableData({
        currentPage: 1,
        fundsPerPage: tablePaginationObj.rowsPerPage,
        sort: orderBy,
        order
      })
    } catch (e) {
      console.log("Error:", e)
    } finally {
      setTableDataLoading(false)
    }
  }

  const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setTableDataLoading(true)

      await fetchTableData({
        currentPage: 1,
        fundsPerPage: Number(event.target.value),
        sort: tableSortingObj.orderBy,
        order: tableSortingObj.order
      })
    } catch (e) {
      console.log("Error:", e)
    } finally {
      setTableDataLoading(false)
    }
  }

  const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    try {
      setTableDataLoading(true)

      await fetchTableData({
        currentPage: page + 1,
        fundsPerPage: tablePaginationObj.rowsPerPage,
        sort: tableSortingObj.orderBy,
        order: tableSortingObj.order
      })
    } catch (e) {
      console.log("Error:", e)
    } finally {
      setTableDataLoading(false)
    }
  }
  const handleReportDownload = async (jobId: string, clientName: string) => {
    try {
      setIsDownloading(true)
      handleSideSectionDisabled(true)

      setSelectedJobIdForDownload(jobId)

      const payload = { jobId }

      const response = await (dispatch(clientStatementDownload(payload)) as unknown as Promise<any>)

      if (response.type === CONTENT_TYPE.PDF) {
        enqueueSnackbar('File downloaded successfully.', {
          variant: 'success',
          autoHideDuration: 5000,
        });
        reportDownload(response as unknown as Blob, `${clientName ?? ""}-ClientStatement.pdf`);
      } else {
        enqueueSnackbar('File download failed!.', {
          variant: 'error',
          autoHideDuration: 5000,
        });
      }

    } catch (e) {
      console.log("Error:", e)
    } finally {
      setIsDownloading(false)
      handleSideSectionDisabled(false)
      setSelectedJobIdForDownload(null)
    }
  }

  const handleFilterTypeChange = ({ target: { value } }: SelectChangeEvent<unknown>) => {
    if (!formikRef?.current) {
      return
    }

    const { values: formikValues, setValues } = formikRef.current

    const modifiedReportSections = [...(formikValues?.reportSections ?? [])]

    if (`${value ?? ""}`.toLowerCase() === "family") {
      modifiedReportSections.push("4")
    }

    setValues({
      ...formikValues,
      filterType: `${value ?? ""}`,
      isHeldAway: false,
      ...((`${value ?? ""}`.toLowerCase() === "family") ? {
        familyName: familyName ?? ""
      } : {
        familyName: ""
      }),
      reportSections: (modifiedReportSections)
        .filter(key => {
          if (`${value ?? ""}`.toLowerCase() !== "family") {
            return key !== "4"
          }
          return true
        })
    })
  }

  return (
    <>
      <ReportsRightLayout>
        <Typography
          variant='h2'
          sx={{
            color: 'text.primary',
            lineHeight: '25px',
            letterSpacing: '0.01px',
            mb: '40px',

          }}
        >
          Client Statement
        </Typography>
        <Formik
          innerRef={formikRef}
          validationSchema={validationSchemaClientStatement}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values }) => (
            <Form>
              <Grid container rowSpacing={2}>
                <Grid item container columnSpacing={3} rowSpacing={2}>
                  <Grid item xs={12} lg={6}>
                    {(isNavbarInfoLoading) && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {(!isNavbarInfoLoading) && (
                      <MFTextField
                        label='Client Code'
                        placeholder={'Client Code'}
                        name={'clientCode'}
                        value={investorClientId}
                        disabled={true}
                      />
                    )}
                  </Grid>
                  {/* Filter Type Dropdown start */}
                  <Grid item xs={12} lg={6}>
                    {(isNavbarInfoLoading) && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {(!isNavbarInfoLoading) && (
                      <MFSelectField
                        name='filterType'
                        label="Filter Type *"
                        items={values?.filterTypeOptions ?? []}
                        disabled={
                          isDownloading ||
                          isNavbarInfoLoading ||
                          isInitiating
                        }
                        onChange={handleFilterTypeChange}
                      />
                    )}
                  </Grid>
                  {/* Filter Type Dropdown end */}
                  {/* Family Name start */}
                  {(`${values?.filterType ?? ""}`.toLowerCase() === "family") && (
                    <Grid item xs={12} lg={6}>
                      <MFTextField
                        name="familyName"
                        label="Family Name"
                        placeholder=""
                        disabled={true}
                        value={values?.familyName ?? ""}
                      />
                    </Grid>
                  )}
                  {/* Family Name end */}
                  {/* portfolio selection  */}
                  {`${values?.filterType ?? ""}`.toLowerCase() === "portfolio" && (
                    <Grid item xs={12} lg={6}>
                      {(isNavbarInfoLoading) && <Skeleton
                        animation={'wave'}
                        height={90}

                      />}
                      {(!isNavbarInfoLoading) && (
                        <MFTextField
                          label='Portfolio'
                          placeholder={'Portfolio'}
                          name={'portfolioCode'}
                          value={clientInterfaceCode}
                          disabled={true}
                        />
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} lg={6}>
                    {(isNavbarInfoLoading) && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {(!isNavbarInfoLoading) && (
                      <>
                        <TextDatePicker
                          name="endDate"
                          label="As on date *"
                          placeholder=""
                          maxDate={new Date(formattedBusinessDate)}
                          disabled={
                            isDownloading ||
                            isNavbarInfoLoading ||
                            isInitiating
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: "10px",
                            marginTop: "-10px"
                          }}>
                          <InfoIconOutlined />
                          <Typography
                            variant='subtitle_600'
                            sx={(theme: Theme) => ({
                              color: theme.palette.grey[500]
                            })}
                          >
                            Reports are generated Since Inception.
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Grid>
                  {/* <Grid item xs={12} lg={6}>
                    {(isNavbarInfoLoading) && <Skeleton
                      animation={'wave'}
                      height={90}
                    />}
                    {(!isNavbarInfoLoading) && (
                      <FormMultiSelect
                        name="reportSections"
                        label='Select Report Sections *'
                        options={Object.keys(REQUESTED_REPORTS)
                          .filter(key => {
                            if ((values?.filterType ?? "").toLowerCase() !== "family") {
                              return key !== "4"
                            }
                            return true
                          })
                          .map(key => ({
                            label: REQUESTED_REPORTS[key],
                            value: key
                          }))}
                        sx={{
                          "&.MuiOutlinedInput-root": {
                            "& fieldset>legend": {
                              fontSize: { xs: "0.6em", md: "0.7em", lg: "0.8em" }
                            }
                          }
                        }}
                        disabled={
                          isDownloading ||
                          isNavbarInfoLoading ||
                          isInitiating
                        }
                      />
                    )}
                  </Grid> */}
                  {/* HeldAway Checkbox start */}
                  {(`${values?.filterType ?? ""}`.toLowerCase() !== "portfolio") && (
                    <Grid
                      item
                      container
                      xs={12}
                      lg={6}
                      sx={{ alignItems: "center" }}
                    >
                      <Grid item>
                        <MFCheckbox
                          name="isHeldAway"
                          label="Include Held Away"
                          sx={(theme: Theme) => ({
                            display: 'flex',
                            alignItems: 'flex-start',
                            color: `${theme.palette.text.primary}`,
                            "& :disabled": {
                              color: `${theme.palette.text.secondary}`,
                            }
                          })}
                          disabled={isDownloading || isNavbarInfoLoading || isInitiating}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {/* HeldAway Checkbox end */}
                </Grid>
                <Grid item container columnSpacing={3}>
                  <Grid item xs={8} />
                  <Grid item xs={4}>
                    {(isNavbarInfoLoading) && <Skeleton
                      animation={'wave'}
                      height={90}

                    />}
                    {(!isNavbarInfoLoading) && <MFSubmitButton
                      label="Initiate"
                      loadingEnable={isInitiating}
                      disabled={isDownloading}
                    />}
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
        <Divider sx={{ mt: 4, mb: 4 }} />
        <Box sx={{ marginTop: 1 }}>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: "10px"
              }}
            >
              <Box>
                <Typography
                  variant='body_600'
                  sx={(theme: Theme) => ({
                    color: theme.palette.text.primary
                  })}
                >
                  Existing Client Statements
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: "5px",
                    marginLeft: '-2px',
                    mt: 0.5
                  }}>
                  <InfoIconOutlined />
                  <Typography
                    variant='subtitle_600'
                    sx={(theme: Theme) => ({
                      color: theme.palette.grey[500]
                    })}
                  >
                    Statements will be available for 1 day only.
                  </Typography>
                </Box>
              </Box>
              <IconButton
                sx={{
                  cursor: 'pointer',
                  "&: disabled": {
                    opacity: 0.5
                  }
                }}
                onClick={handleRefresh}
                disabled={isTableDataLoading}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
            <Box sx={{ marginTop: 5 }}>
              <ClientStatementTable
                isLoading={isTableDataLoading}
                tableData={tableData}
                handleTableSort={handleTableSort}
                tablePaginationObj={tablePaginationObj}
                handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                handleTablePageChange={handleTablePageChange}
                tableSortingObj={tableSortingObj}
                handleReportDownload={handleReportDownload}
                isDownloading={isDownloading}
                selectedJobIdForDownload={selectedJobIdForDownload}
              />
            </Box>
          </Box>
        </Box>
      </ReportsRightLayout>
      <Dialog
        onClose={handleNoInvestorDialogClose}
        open={isNoInvestorDialogVisible}
        maxWidth='sm'
        sx={(theme: Theme) => ({
          '& .MuiPaper-root': {
            borderRadius: '12px',
            backgroundColor: theme.palette.common.white,
            padding: "22px 24px",
            width: "533px",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Box
              sx={{
                display: "flex",
                columnGap: "12px",
                alignItems: "center"
              }}
            >
              <YellowWarning />
              <Typography
                variant="h2"
                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
              >
                No Investor Found!!
              </Typography>
            </Box>
            <IconButton
              aria-label="close"
              onClick={handleNoInvestorDialogClose}
              sx={{
                padding: "5px"
              }}
            >
              <DialogCloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ClientStatement;
